import { colors } from '@lmc/common/constants'
import { DefaultLmc, theme } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import MultiSelectField from 'components/fields/types/MultiSelectField'
import {
  type RiskAssessmentListItem,
  useRiskAssessments
} from 'hooks/useRiskAssessments'
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { EditTooltip } from 'screens/Residents/screens/ResidentProfileScreen/components/EditTooltip'
import styled from 'styled-components'

export type RiskAssessmentSelectorVariant = 'default' | 'consent'

type RiskAssessmentOptionData = {
  id: string
  name: string
  reviewedOn?: string
}

export interface RiskAssessmentSelectorProps {
  residentId?: string
  selectedRiskAssessments: string[]
  onChange: (ids: string[]) => void
  readonly?: boolean
  label?: React.ReactNode
  variant?: RiskAssessmentSelectorVariant
}

const getRiskAssessmentName = (item: RiskAssessmentListItem) =>
  item.name || item.form?.name || 'Untitled'

export const RiskAssessmentSelector: React.FC<RiskAssessmentSelectorProps> = ({
  residentId,
  selectedRiskAssessments,
  onChange,
  readonly = false,
  label,
  variant = 'default'
}) => {
  const location = useLocation()
  const { data: riskAssessments, isLoading } = useRiskAssessments({
    residentId: residentId || '',
    enabled: !!residentId
  })

  // Options offered in the dropdown — archived RAs can't be newly linked.
  const options: RiskAssessmentOptionData[] = React.useMemo(
    () =>
      riskAssessments
        .filter((ra) => !ra.archived)
        .map((ra) => ({
          id: ra.id,
          name: getRiskAssessmentName(ra),
          reviewedOn: ra.reviewedOn
        })),
    [riskAssessments]
  )

  // Currently-selected items — resolve from the FULL list (including archived)
  // so an already-linked RA that was archived later doesn't silently drop on
  // save. Archived items are labelled visually.
  const selectedOptions: RiskAssessmentOptionData[] | null =
    React.useMemo(() => {
      if (!selectedRiskAssessments?.length) {
        return []
      }
      if (isLoading) {
        return null
      }
      return selectedRiskAssessments
        .map((id): RiskAssessmentOptionData | undefined => {
          const ra = riskAssessments.find((item) => item.id === id)
          if (!ra) return undefined
          const name = getRiskAssessmentName(ra)
          return {
            id: ra.id,
            name: ra.archived ? `${name} (Archived)` : name,
            reviewedOn: ra.reviewedOn
          }
        })
        .filter((option): option is RiskAssessmentOptionData => !!option)
    }, [selectedRiskAssessments, riskAssessments, isLoading])

  const formatOption = (option: RiskAssessmentOptionData) => (
    <OptionWrapper>
      <IconContainer>
        <DefaultLmc />
      </IconContainer>
      <OptionTitle>{option.name}</OptionTitle>
    </OptionWrapper>
  )

  const handleChange = (selected: RiskAssessmentOptionData[]) => {
    onChange(selected.map((item) => item.id))
  }

  if (readonly) {
    return (
      <SelectorWrapper $variant={variant}>
        <ReadonlyWrapper>
          {label}
          {selectedOptions === null ? (
            <Text level={1.5}>--</Text>
          ) : selectedOptions.length > 0 ? (
            <ReadonlyIconsContainer>
              {selectedOptions.map((option) => (
                <EditTooltip
                  key={option.id}
                  title={
                    <TooltipContent>
                      <IconContainer>
                        <DefaultLmc />
                      </IconContainer>
                      {option.name}
                    </TooltipContent>
                  }
                >
                  <ChipLink
                    to={`/residents/${residentId}/risk-assessments/${option.id}`}
                    state={{ referrer: location.pathname }}
                  >
                    <IconContainer>
                      <DefaultLmc />
                    </IconContainer>
                  </ChipLink>
                </EditTooltip>
              ))}
            </ReadonlyIconsContainer>
          ) : (
            <Text level={1.5}>Not linked to any risk assessment</Text>
          )}
        </ReadonlyWrapper>
      </SelectorWrapper>
    )
  }

  if (selectedOptions === null) {
    return (
      <SelectorWrapper $variant={variant}>
        <Text level={1.5}>--</Text>
      </SelectorWrapper>
    )
  }

  return (
    <SelectorWrapper $variant={variant}>
      <MultiSelectField
        formKey={undefined}
        label={label}
        customOnChange={handleChange}
        onChange={() => {}}
        options={options}
        formatOptionLabel={formatOption}
        customValues={selectedOptions}
        centerLabel
        errors={undefined}
        style={null}
        placeholder=""
        values={[]}
      />
    </SelectorWrapper>
  )
}

const SelectorWrapper = styled.div<{
  $variant: RiskAssessmentSelectorVariant
}>`
  margin-top: 10px;
  background: ${colors.white};
  border-radius: 15px;
  padding: ${({ $variant }) => ($variant === 'consent' ? '8px 16px' : '15px')};
  display: flex;
`

const ReadonlyWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const OptionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

const OptionTitle = styled(Text)`
  font-weight: 500;
  color: ${theme.color.neutral.color2};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ReadonlyIconsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const TooltipContent = styled.div`
  display: flex;
  align-items: center;
`

const ChipLink = styled(Link)`
  display: inline-flex;
  color: inherit;
  text-decoration: none;
`

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  height: 28px;
  margin-right: 5px;
  background-color: ${({ theme }) => theme.color.status.secondary};
  border-radius: 4px;
`
